import { useCallback, useContext, useMemo } from "react";

import { useLogout } from "@/generated/open-api/customer/customer";
import { clearClientOutletCart } from "@/storage/clearClientOutletCart";
import { LocalStagePath } from "@/storage/types";
import { setLocalStorageValue } from "@/storage/useClientLocalStorage/helpers";

import { AuthContext } from "./AuthProvider";

type UseAuthType =
  | {
      accessToken: string;
      isLoggedIn: true;
      setAccessToken: (accessToken: string) => Promise<void>;
      clearAccessToken: () => Promise<void>;
      logout: () => Promise<void>;
      logoutWithoutRedirect: () => Promise<void>;
    }
  | {
      accessToken: null;
      isLoggedIn: false;
      setAccessToken: (accessToken: string) => Promise<void>;
      clearAccessToken: () => Promise<void>;
      logout: () => Promise<void>;
      logoutWithoutRedirect: () => Promise<void>;
    };

/**
 *
 * @remarks
 * ClientSide、ServerSideどちらでも利用可能だが、Static Renderingでなくなるので注意
 */
export const useAuth = (): UseAuthType => {
  const { accessToken, setAccessToken } = useContext(AuthContext);

  const clearAccessToken = useCallback(async () => {
    await setAccessToken(null);
  }, [setAccessToken]);

  const { mutateAsync: logoutFunc } = useLogout();

  const logout = useCallback(async () => {
    await logoutFunc();
    await clearAccessToken();
    clearStoredCustomer();
    clearClientOutletCart();
    window.location.href = "/";
  }, [clearAccessToken, logoutFunc]);

  const logoutWithoutRedirect = useCallback(async () => {
    await logoutFunc();
    await clearAccessToken();
    clearClientOutletCart();
  }, [clearAccessToken, logoutFunc]);

  return useMemo(() => {
    return {
      accessToken,
      isLoggedIn: !!accessToken,
      setAccessToken,
      clearAccessToken,
      logout,
      logoutWithoutRedirect,
    } as UseAuthType;
  }, [accessToken, clearAccessToken, logout, logoutWithoutRedirect, setAccessToken]);
};

const customerStoreKey: LocalStagePath = `vuex.customer.customer`;

function clearStoredCustomer() {
  setLocalStorageValue(customerStoreKey, undefined);
}
